import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Taekwondo', path: '/taekwondo' },
  { name: 'History', path: '/history' },
  { name: 'Team', path: '/team' },
  { name: 'Schedule', path: '/schedule' },
  { name: 'Media', path: '/media' },
  { name: 'Contact', path: '/contact' }
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, profile, signOut, isAdmin } = useAuth();

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  useEffect(() => {
    closeMenu();
  }, [location]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const getProtectedLinks = () => {
    const links = [];
    
    if (profile?.is_approved) {
      links.push({ name: 'Members Area', path: '/members' });
      
      if (isAdmin) {
        links.push({ name: 'Admin Area', path: '/admin' });
      }
    }
    
    return links;
  };

  const protectedLinks = getProtectedLinks();

  return (
    <nav
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300 py-3',
        scrolled ? 'bg-white/90 backdrop-blur-md shadow-md' : 'bg-transparent'
      )}
    >
      <div className="container mx-auto px-4 flex justify-between items-center">
        <Link to="/" className="flex items-center space-x-2" onClick={closeMenu}>
          <span className="font-display font-bold text-2xl">
            <span className="text-taekwondo-red">OMAC-UK</span>
          </span>
          <span className="hidden sm:inline-block font-semibold">Taekwondo</span>
        </Link>

        <div className="hidden lg:flex space-x-1 items-center">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={cn(
                'px-3 py-2 rounded-md text-sm font-medium transition-colors',
                location.pathname === link.path
                  ? 'text-taekwondo-red font-semibold'
                  : 'text-gray-700 hover:text-taekwondo-red'
              )}
            >
              {link.name}
            </Link>
          ))}
          
          {protectedLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={cn(
                'px-3 py-2 rounded-md text-sm font-medium transition-colors',
                location.pathname === link.path
                  ? 'text-taekwondo-red font-semibold'
                  : 'text-gray-700 hover:text-taekwondo-red'
              )}
            >
              {link.name}
            </Link>
          ))}

          <div className="ml-4 flex items-center">
            {user ? (
              <Button 
                variant="destructive" 
                size="sm" 
                className="flex items-center gap-1 bg-taekwondo-red hover:bg-taekwondo-red/90"
                onClick={() => signOut()}
              >
                <LogOut size={16} />
                Logout
              </Button>
            ) : (
              <Button 
                variant="default" 
                size="sm" 
                className="bg-taekwondo-blue hover:bg-taekwondo-blue/90 text-white"
                asChild
              >
                <Link to="/auth/login">Login</Link>
              </Button>
            )}
          </div>
        </div>

        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={toggleMenu}
          aria-label="Toggle Menu"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </Button>
      </div>

      <div
        className={cn(
          'lg:hidden absolute w-full bg-white shadow-lg transition-all duration-300 ease-in-out overflow-y-auto max-h-[calc(100vh-4rem)]',
          isOpen ? 'opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
        )}
      >
        <div className="container mx-auto px-4 py-3 flex flex-col space-y-2">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={cn(
                'px-4 py-3 rounded-md text-base font-medium transition-colors',
                location.pathname === link.path
                  ? 'bg-gray-100 text-taekwondo-red font-semibold'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-taekwondo-red'
              )}
              onClick={closeMenu}
            >
              {link.name}
            </Link>
          ))}
          
          {protectedLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={cn(
                'px-4 py-3 rounded-md text-base font-medium transition-colors',
                location.pathname === link.path
                  ? 'bg-gray-100 text-taekwondo-red font-semibold'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-taekwondo-red'
              )}
              onClick={closeMenu}
            >
              {link.name}
            </Link>
          ))}
          
          <div className="pt-2 border-t border-gray-100">
            {user ? (
              <Button 
                variant="destructive" 
                className="w-full justify-start px-4 py-3 rounded-md text-base font-medium flex items-center bg-taekwondo-red hover:bg-taekwondo-red/90"
                onClick={() => { signOut(); closeMenu(); }}
              >
                <LogOut className="mr-2" size={18} />
                Logout
              </Button>
            ) : (
              <Link
                to="/auth/login"
                className="block px-4 py-3 rounded-md text-base font-medium bg-taekwondo-blue text-white hover:bg-taekwondo-blue/90"
                onClick={closeMenu}
              >
                Login
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
